import React, { useState } from 'react';
import styled from 'styled-components';

const ContactContainer = styled.section`
  background-color: #4B0000;
  background-image: linear-gradient(to bottom, #4B0000, #000000);
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: clamp(2.2rem, 5vw, 2.8rem);
  font-weight: 700;
  position: relative;
  padding-bottom: 1.5rem;
  letter-spacing: 1px;
  color: #f9d776;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 5px;
    background: linear-gradient(90deg, #88290d, #b45128);
    border-radius: 3px;
    box-shadow: 0 0 12px rgba(180, 81, 40, 0.5);
  }
`;

const SectionIntro = styled.p`
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 4rem;
  font-size: clamp(1rem, 1.8vw, 1.15rem);
  color: #dfad8c;
`;

const ContactForm = styled.form`
  max-width: 700px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #FFFFE0;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 1rem;
  border-radius: var(--border-radius);
  background: rgba(139, 0, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.3);
  color: #FFFFE0;
  font-family: var(--font-primary);
  transition: border-color var(--transition-speed) var(--transition-effect),
              box-shadow var(--transition-speed) var(--transition-effect);
  
  &:focus {
    outline: none;
    border-color: #FFA500;
    box-shadow: 0 0 10px rgba(255, 165, 0, 0.3);
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border-radius: var(--border-radius);
  background: rgba(139, 0, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.3);
  color: #FFFFE0;
  font-family: var(--font-primary);
  min-height: 150px;
  resize: vertical;
  transition: border-color var(--transition-speed) var(--transition-effect),
              box-shadow var(--transition-speed) var(--transition-effect);
  
  &:focus {
    outline: none;
    border-color: #FFA500;
    box-shadow: 0 0 10px rgba(255, 165, 0, 0.3);
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  padding: 1rem 2.5rem;
  background: #FFA500;
  color: #000000;
  font-weight: 600;
  border-radius: var(--border-radius);
  transition: background var(--transition-speed) var(--transition-effect),
              color var(--transition-speed) var(--transition-effect),
              transform var(--transition-speed) var(--transition-effect),
              box-shadow var(--transition-speed) var(--transition-effect);
  border: 2px solid #FFA500;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-top: 1rem;
  box-shadow: 0 5px 15px rgba(255, 165, 0, 0.2);
  
  &:hover {
    background: transparent;
    color: #FFD700;
    transform: translateY(-5px) scale(1.03);
    box-shadow: 0 10px 25px rgba(255, 165, 0, 0.3);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  margin-top: 4rem;
`;

const ContactCard = styled.div`
  background: rgba(30, 29, 27, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid rgba(180, 81, 40, 0.2);
  transition: transform var(--transition-speed) var(--transition-effect),
              box-shadow var(--transition-speed) var(--transition-effect);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(180, 81, 40, 0.2);
    border-color: #b45128;
  }
`;

const ContactIcon = styled.div`
  font-size: 1.8rem;
  color: #b45128;
`;

const ContactDetails = styled.div`
  h4 {
    margin-bottom: 0.3rem;
    color: var(--heading-text);
  }
  
  p {
    margin-bottom: 0;
    color: var(--secondary-text);
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically handle form submission
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    // Show success message
    alert('Message sent successfully!');
  };
  
  return (
    <ContactContainer id="contact">
      <SectionTitle>Get In Touch</SectionTitle>
      <SectionIntro>
        Have a project in mind or want to collaborate? Feel free to reach out using the form below or through any of my contact channels.
      </SectionIntro>
      
      <ContactForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Name</Label>
          <Input 
            type="text" 
            id="name" 
            name="name" 
            value={formData.name}
            onChange={handleChange}
            required 
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input 
            type="email" 
            id="email" 
            name="email" 
            value={formData.email}
            onChange={handleChange}
            required 
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="subject">Subject</Label>
          <Input 
            type="text" 
            id="subject" 
            name="subject" 
            value={formData.subject}
            onChange={handleChange}
            required 
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="message">Message</Label>
          <TextArea 
            id="message" 
            name="message" 
            value={formData.message}
            onChange={handleChange}
            required 
          />
        </FormGroup>
        
        <SubmitButton type="submit">Send Message</SubmitButton>
      </ContactForm>
      
      <ContactInfo>
        <ContactCard>
          <ContactIcon>
            <i className="fas fa-envelope"></i>
          </ContactIcon>
          <ContactDetails>
            <h4>Email</h4>
            <p>contact@example.com</p>
          </ContactDetails>
        </ContactCard>
        
        <ContactCard>
          <ContactIcon>
            <i className="fas fa-phone"></i>
          </ContactIcon>
          <ContactDetails>
            <h4>Phone</h4>
            <p>+1 (123) 456-7890</p>
          </ContactDetails>
        </ContactCard>
        
        <ContactCard>
          <ContactIcon>
            <i className="fas fa-map-marker-alt"></i>
          </ContactIcon>
          <ContactDetails>
            <h4>Location</h4>
            <p>Mumbai, India</p>
          </ContactDetails>
        </ContactCard>
      </ContactInfo>
    </ContactContainer>
  );
};

export default Contact;